import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import {
  ColumnDetails,
  ColumnKeys,
  ColumnsOrder,
  ViewConfiguration,
  ViewType,
} from '@nirvana/shared/dtos/types/alerts/common';
import { GridViewWebComponent } from '../../grid/gridView';
import { TemplateState } from '../../types/common/types';
import {
  WEB_COMPONENT_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../webcomponents.constants';
import { categoryRenderer, companyRenderer, lastDetectedHeaderRenderer } from './customGridRenderers';
import { Column } from '@nirvana/shared/dtos/types/shared/filters';
import { AlertsGridDataProvider } from '../dataProvider/AlertsGridDataProvider';
import { InitAlertsResponseFrontend } from '../types';
import { gridCustomHtmlRenderer } from '../../grid/utils/renderers';
import { getLabelForEnumValue } from '../../grid/utils/columns';
import {
  SectionType,
  SmartViewInlineActions,
  SmartViewItemConfiguration,
  SmartViewSection,
} from '../../grid/gridSmartViews/types';
import { ColumnDetails as ColumnDetailsGeneric } from '../../grid/types/common';
import { GridSmartViewsWebComponent } from '../../grid/gridSmartViews';
import { ColumnConfiguration } from '@nirvana/shared/models/nestjs';
import { GzPushBodyLayoutWebComponent } from '../../ui/gzPushBodyLayout';
import { RequiredValidator } from '../../ui/gzForm/validators/required.validator';
import { RemoteValidator } from '../../ui/gzForm/validators/remote.validator';
import { ValidateViewNameResponse } from '@nirvana/shared/dtos/types/alerts/gz-console';
import { htmlDecode } from '../../utils/escape.utils';
import { GridTitleConfig } from '../../grid/uiElements/gridViewTitle/types';
import { AlertsDetailsPanelComponent } from '../../alertsDetailsPanel';
import { GzSideMenuWebComponent } from '../../ui/gzSideMenu';

export class AlertsView extends BaseWebComponentView {
  protected commonStrings: any;
  protected inlineActionsStrings: any;
  protected smartViewsStrings: any;
  private flexibleGrowingColumn = ColumnKeys.DETAILS;
  private alertsGridDataProvider: AlertsGridDataProvider;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('gz-push-body-layout .wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');
    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<Column, ColumnKeys>;
    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_SMART_VIEWS}`
    ) as GridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$detailsPanel = this.shadow.querySelector('gz-push-body-layout #detailsPanel') as GzSideMenuWebComponent;
    this.getTranslations();
    this.setTranslatedStrings();

    // getDomElements is called at start, and also when the component finished initializing all its children.
    // Its only in this second step that we have the grid initialized.
    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }
  }

  getTranslations() {
    return {
      commonStrings: this.commonStrings,
      inlineActionsStrings: this.inlineActionsStrings,
      smartViewsStrings: this.smartViewsStrings,
    };
  }

  setTranslatedStrings() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.inlineActionsStrings = this.stringsTranslator.getInlineActionsCommonStrings();
    this.smartViewsStrings = this.stringsTranslator.getSmartViewsTranslations();
    this.dom.$loadingErrorMessage.innerHTML = this.commonStrings.generalSystemError;
    this.dom.$smartViewsTitle.innerHTML = this.smartViewsStrings.panelTitle.toUpperCase();
  }

  buildSuccessView(data: InitAlertsResponseFrontend) {
    this.resetTemplateState(this.dom.$wrapper);

    if (!data) {
      this.buildErrorView();
      return;
    }

    this.buildGridView(
      data.views,
      data.activeViewId,
      data.allColumns,
      data.defaultViewProps,
      data.isDrillDown,
      data.isPartner,
      data.filterKeys,
      data.defaultSelectedTargetIds,
      data.allDirectManagement
    );
    this.buildSmartViews(data.views, data.activeViewId, data.defaultViewProps);
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public buildGridView(
    views: Array<ViewConfiguration>,
    activeViewId: string,
    allColumns: Array<ColumnDetails>,
    defaultViewProps: ViewConfiguration,
    isDrillDown: boolean,
    isPartner: boolean,
    filterKeys: Array<ColumnKeys>,
    defaultSelectedTargets: Array<string>,
    allDirectManagement?: Array<string>
  ) {
    const view = this.getView(views, activeViewId);
    const gridTitle = view.name;

    if (view.type === ViewType.DEFAULT) {
      view.displayedColumns.sort((column1, column2) => {
        return ColumnsOrder[Column[column1]] - ColumnsOrder[Column[column2]];
      });
    }

    if (!this.alertsGridDataProvider) {
      this.alertsGridDataProvider = new AlertsGridDataProvider(
        this.dependencies,
        allColumns,
        defaultSelectedTargets,
        isPartner,
        allDirectManagement
      );
    }

    this.dom.$grid.dataProvider = this.alertsGridDataProvider;

    this.dom.$grid.headerRenderers = {
      [ColumnKeys.LAST_DETECTED]: lastDetectedHeaderRenderer,
    };

    this.dom.$grid.columnRenderers = {
      [ColumnKeys.ACTION_TAKEN]: this.actionTakenRenderer,
      [ColumnKeys.CATEGORY]: categoryRenderer,
      [ColumnKeys.COMPANY]: companyRenderer,
    };

    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.allColumnDetails = allColumns;
    this.setGridViewProps(view, defaultViewProps.columnConfigs);
    this.dom.$grid.predefinedFiltersKeys = filterKeys;
    this.dom.$grid.defaultViewProps = defaultViewProps;
    this.dom.$grid.defaultFlexGrowColumn = this.flexibleGrowingColumn;
    this.dom.$grid.shouldLoadAsync = true;
    this.dom.$grid.enableCSVExport = true;
    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$grid.smartViewsEnabled = true;
    this.dom.$grid.smartViewDiscardButtonDisabledState = !isDrillDown;
    this.dom.$grid.activeViewProps = view;
    this.dom.$grid.activeViewInitialProps = { ...view };
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.titleConfig = isDrillDown ? new GridTitleConfig(gridTitle + '*') : new GridTitleConfig(gridTitle);
    this.dom.$grid.showConfigurableArea = true;
    this.dom.$grid.propertiesSet = true;
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
  }

  private getView(views: Array<ViewConfiguration>, activeViewId: string): ViewConfiguration {
    for (const view of views) {
      if (view.id === activeViewId) {
        return view;
      }
    }

    return views[0];
  }

  public actionTakenRenderer = (rowData, columnDetails: ColumnDetailsGeneric<Column, ColumnKeys>) => {
    const cellValue = rowData.item[columnDetails.key];

    if (typeof cellValue !== 'object' || cellValue.action === undefined || cellValue.numTimes <= 0) {
      throw new Error(`Unexpected value for action taken: ${JSON.stringify(cellValue, null, 2)}`);
    }

    const actionLabel = getLabelForEnumValue(columnDetails, cellValue.action);
    return gridCustomHtmlRenderer(
      `
        <span class="cell-label">${actionLabel} (${this.dependencies.translateFn('{numTimes} times', {
        numTimes: cellValue.numTimes,
      })})</span>
`
    );
  };

  private buildSmartViews(data: Array<ViewConfiguration>, activeViewId: string, defaultViewProps: ViewConfiguration) {
    if (!(this.dom.$gridSmartViews instanceof GridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.propertiesSet = false;

    const defaultViews: Array<SmartViewItemConfiguration> = this.getViewsByType(data, ViewType.DEFAULT);
    const customViews: Array<SmartViewItemConfiguration> = this.getViewsByType(data, ViewType.CUSTOM).map(view => ({
      ...view,
      // We have to do this because the view names are encoded in smart views & Delete dialogs.
      viewName: htmlDecode(view.viewName),
    }));

    this.dom.$gridSmartViews.activeViewId = activeViewId;
    this.dom.$gridSmartViews.sections = [
      {
        sectionTitle: this.dependencies.translateFn('Standard'),
        type: SectionType.DEFAULT,
        inlineActions: [],
        items: defaultViews.map(smartViewConfiguration => {
          if (smartViewConfiguration.id !== defaultViewProps.id) {
            return smartViewConfiguration;
          }
          // We need to rebuild the default view here since a drilldown would set the filters and columns to different values
          return {
            id: defaultViewProps.id,
            viewName: defaultViewProps.name,
            viewType: defaultViewProps.type,
            filters: defaultViewProps.filters,
            displayedColumns: defaultViewProps.displayedColumns,
            isCompactView: defaultViewProps.compactView,
            pageSize: defaultViewProps.pageSize,
            columnConfigs: defaultViewProps.columnConfigs,
          };
        }),
      },
      {
        sectionTitle: this.dependencies.translateFn('Custom'),
        type: SectionType.SAVED_VIEWS,
        smartViewMaxInputLength: 64,
        noSpaceEscape: true,
        inlineActions: [
          {
            title: this.inlineActionsStrings.rename,
            value: SmartViewInlineActions.RENAME,
          },
          {
            title: this.inlineActionsStrings.delete,
            value: SmartViewInlineActions.DELETE,
            customClass: 'danger',
          },
        ],
        items: customViews,
        smartViewValidators: [
          RequiredValidator('This field is required.'),
          RemoteValidator(
            (value: string) => {
              const viewName = value.trimEnd().trimStart();
              return this.dependencies.api.clients.Alerts.validateViewName({ viewName: viewName });
            },
            (response: ValidateViewNameResponse) => response.valid,
            this.dependencies.translateFn('A view with this name already exists.'),
            this.dependencies.translateFn('A communication error has occurred. Please try again later.')
          ),
        ],
      },
    ] as Array<SmartViewSection>;

    this.dom.$gridSmartViews.propertiesSet = true;
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);
    this.dom.$gridSmartViews.start().catch(console.error);
    this.setElementClass(this.dom.$loader, 'hidden');
    this.removeElementClass(this.dom.$gridSmartViews, 'hidden');
  }

  private getViewsByType(views: Array<ViewConfiguration>, viewType: ViewType): Array<SmartViewItemConfiguration> {
    const filteredViews: Array<SmartViewItemConfiguration> = views
      .filter((viewConfiguration: ViewConfiguration) => viewConfiguration.type === viewType)
      .map((viewConfiguration: ViewConfiguration) => {
        return {
          id: viewConfiguration.id,
          viewName: viewConfiguration.name,
          viewType: viewConfiguration.type,
          isCompactView: viewConfiguration.compactView,
          columnConfigs: viewConfiguration.columnConfigs,
          displayedColumns: viewConfiguration.displayedColumns,
          filters: viewConfiguration.filters,
          pageSize: viewConfiguration.pageSize,
        };
      });

    return filteredViews ?? [];
  }

  public setGridViewProps(
    viewConfiguration: ViewConfiguration,
    defaultColumnConfigs: Array<ColumnConfiguration> | undefined
  ) {
    this.dom.$grid.viewId = viewConfiguration.id;
    this.dom.$grid.columns = viewConfiguration.displayedColumns;
    this.dom.$grid.filters = viewConfiguration.filters;
    this.dom.$grid.isCompactViewChecked = viewConfiguration.compactView;
    this.dom.$grid.pageSize = viewConfiguration.pageSize;
    this.dom.$grid.columnConfigs = viewConfiguration.columnConfigs
      ? viewConfiguration.columnConfigs
      : defaultColumnConfigs;
  }

  public buildDetailsPanel(
    eventId: string,
    allColumns: Array<ColumnDetails>,
    isPartner: boolean
  ): AlertsDetailsPanelComponent {
    const detailsPanel = new AlertsDetailsPanelComponent();
    detailsPanel.eventId = eventId;
    detailsPanel.allColumnDetails = allColumns;
    detailsPanel.isPartner = isPartner;
    detailsPanel.propertiesSet = true;
    detailsPanel.injectDependencies(this.dependencies);
    detailsPanel.start().catch(console.error);

    return detailsPanel;
  }
}
